import {
  LEAD_TAGS_MAX_PER_LEAD,
  LEAD_TAG_NAME_MAX_LENGTH,
  LeadTagWithCount,
} from 'models/Lead';

import { normalizeTagName } from 'utils/LeadTagHelper';

export type TypedTagResolution =
  | { kind: 'empty' }
  | { kind: 'duplicate'; name: string }
  | { kind: 'existing'; name: string }
  | { kind: 'create'; name: string };

/** Trims, collapses whitespace and caps the length to the server limit. */
export const sanitizeTagName = (raw: string): string =>
  raw.replace(/\s+/g, ' ').trim().slice(0, LEAD_TAG_NAME_MAX_LENGTH).trim();

/** Names added to / removed from the draft, compared by normalized name. */
export const diffTags = (
  initial: string[],
  draft: string[]
): { added: string[]; removed: string[] } => {
  const initialKeys = new Set(initial.map(normalizeTagName));
  const draftKeys = new Set(draft.map(normalizeTagName));
  return {
    added: draft.filter((n) => !initialKeys.has(normalizeTagName(n))),
    removed: initial.filter((n) => !draftKeys.has(normalizeTagName(n))),
  };
};

/**
 * Decides what pressing Enter on the typed text means: reuse an existing
 * catalog tag (keeping its casing), create a new one, or report a duplicate.
 */
export const resolveTypedTag = (
  raw: string,
  results: LeadTagWithCount[],
  draft: string[]
): TypedTagResolution => {
  const name = sanitizeTagName(raw);
  if (!name) return { kind: 'empty' };
  const key = normalizeTagName(name);

  const inDraft = draft.find((n) => normalizeTagName(n) === key);
  if (inDraft) return { kind: 'duplicate', name: inDraft };

  const hit = results.find((r) => normalizeTagName(r.name) === key);
  if (hit) return { kind: 'existing', name: hit.name };

  return { kind: 'create', name };
};

export const canAddTag = (draft: string[]): boolean =>
  draft.length < LEAD_TAGS_MAX_PER_LEAD;
